import React, { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin, AlarmClock, ArrowUpRight, Hourglass } from "lucide-react";
import { COURSE_LOCATION } from "../data/schedule";

const useDeadline = (deadlineISO) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadlineISO) return;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [deadlineISO]);
  if (!deadlineISO) return null;
  const target = new Date(deadlineISO).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return { closed: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  return { closed: false, days, hours };
};

const Countdown = ({ countdown, testId }) => {
  if (!countdown) return null;
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-4 py-2.5"
      data-testid={testId}
    >
      <Hourglass size={16} className="text-amber-400 shrink-0" />
      {countdown.closed ? (
        <span className="text-amber-300 text-sm font-medium">Iscrizioni chiuse per questa edizione</span>
      ) : (
        <span className="text-white/85 text-sm">
          {countdown.days > 0 ? (
            <>Mancano <strong className="text-amber-300">{countdown.days} {countdown.days === 1 ? "giorno" : "giorni"}</strong> alla chiusura iscrizioni</>
          ) : (
            <>Ultime <strong className="text-amber-300">{countdown.hours} ore</strong> per iscriverti!</>
          )}
        </span>
      )}
    </div>
  );
};

/**
 * Scheda "Prossima edizione".
 * - default: layout orizzontale (usato nelle pagine corso, sotto l'hero)
 * - vertical: card prominente verticale (usata nell'hero delle landing, colonna destra)
 */
export const CourseScheduleCard = ({ schedule, onCta, ctaLabel = "Prenota il tuo posto", testId, vertical = false }) => {
  const countdown = useDeadline(schedule?.deadlineISO);
  if (!schedule) return null;

  const seats = typeof schedule.seatsLeft === "number" ? schedule.seatsLeft : null;
  const seatsLow = seats !== null && seats <= 5;
  const seatsLabel = seats !== null ? `${seats === 1 ? "Ultimo posto" : `Restano ${seats} posti`}` : "Posti limitati";
  const seatsTestId = testId ? `${testId}-seats` : "course-schedule-seats";

  if (vertical) {
    const rows = [
      { icon: Clock, label: "Durata", value: schedule.duration },
      { icon: MapPin, label: "Dove", value: COURSE_LOCATION },
      { icon: AlarmClock, label: "Iscrizioni entro", value: schedule.deadline, warn: true },
    ];
    return (
      <div
        data-testid={testId || "course-schedule-card"}
        className="schedule-card-anim relative rounded-3xl border border-[#d4ff3d]/40 bg-[#0e0e0e]/80 backdrop-blur-xl p-6 sm:p-7 overflow-hidden"
      >
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#d4ff3d]/10 blur-3xl" aria-hidden />
        <div className="relative">
          <div className="flex items-center justify-between gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "#d4ff3d", color: "#0a0a0a" }}>
              Prossima edizione
            </span>
            <span
              data-testid={seatsTestId}
              className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${seatsLow ? "text-red-400" : "text-amber-300"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${seatsLow ? "bg-red-500" : "bg-amber-400"}`} /> {seatsLabel}
            </span>
          </div>

          <div className="flex items-start gap-3 mb-5">
            <CalendarDays size={26} className="text-[#d4ff3d] mt-1 shrink-0" />
            <div>
              <div className="text-white/40 text-[10px] uppercase tracking-widest font-mono mb-1">Data del corso</div>
              <div className="font-display text-2xl sm:text-[28px] leading-tight text-white schedule-date-pop">{schedule.date}</div>
              {schedule.time && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[#0a0a0a] text-xs font-semibold px-2 py-0.5 rounded-md" style={{ background: "#d4ff3d" }}>
                  <Clock size={12} /> {schedule.time}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3 border-t border-white/10 pt-4">
            {rows.map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.label} className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide font-mono">
                    <Icon size={14} className={r.warn ? "text-amber-400" : "text-[#d4ff3d]"} /> {r.label}
                  </span>
                  <span className={`text-sm text-right ${r.warn ? "text-amber-300 font-medium" : "text-white/85"}`}>{r.value}</span>
                </div>
              );
            })}
          </div>

          {countdown && <div className="mt-5"><Countdown countdown={countdown} testId={testId ? `${testId}-countdown` : "course-schedule-countdown"} /></div>}

          {onCta && (
            <button
              type="button"
              onClick={onCta}
              data-testid={testId ? `${testId}-cta` : "course-schedule-cta"}
              className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2"
            >
              {ctaLabel} <ArrowUpRight size={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Horizontal (course pages)
  const items = [
    { icon: CalendarDays, label: "Data", value: (
        <>
          {schedule.date}
          {schedule.time && <span className="block text-[#d4ff3d]/90 text-xs font-mono mt-0.5">ore {schedule.time}</span>}
        </>
      ), accent: true },
    { icon: Clock, label: "Durata", value: schedule.duration },
    { icon: MapPin, label: "Dove", value: COURSE_LOCATION },
    { icon: AlarmClock, label: "Iscrizioni entro", value: schedule.deadline, warn: true },
  ];
  return (
    <div
      data-testid={testId || "course-schedule-card"}
      className="relative rounded-2xl border border-[#d4ff3d]/25 bg-gradient-to-br from-[#d4ff3d]/[0.07] to-white/[0.02] p-5 sm:p-6 overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-5">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "#d4ff3d", color: "#0a0a0a" }}>
          Prossima edizione
        </span>
        <span
          data-testid={seatsTestId}
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest ${seatsLow ? "text-red-400" : "text-amber-300"}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${seatsLow ? "bg-red-500" : "bg-amber-400"}`} /> {seatsLabel}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.label} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-white/40 text-[10px] uppercase tracking-widest font-mono">
                <Icon size={13} className={it.warn ? "text-amber-400" : "text-[#d4ff3d]"} /> {it.label}
              </div>
              <div className={`leading-snug ${it.accent ? "text-white font-semibold text-[15px]" : it.warn ? "text-amber-300 text-sm" : "text-white/80 text-sm"}`}>
                {it.value}
              </div>
            </div>
          );
        })}
      </div>

      {countdown && <div className="mt-5"><Countdown countdown={countdown} testId={testId ? `${testId}-countdown` : "course-schedule-countdown"} /></div>}

      {onCta && (
        <button
          type="button"
          onClick={onCta}
          data-testid={testId ? `${testId}-cta` : "course-schedule-cta"}
          className="btn-primary w-full sm:w-auto mt-6 inline-flex items-center justify-center gap-2"
        >
          {ctaLabel} <ArrowUpRight size={18} />
        </button>
      )}
    </div>
  );
};

export default CourseScheduleCard;
